// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFuel } from '../contexts/FuelContext';
import './GravityWellEffect.css';

const GravityWellEffect = ({ onComplete }) => {
  // Use Context API for fuel
  const { fuel } = useFuel();
  
  const [phase, setPhase] = useState('entering'); // entering, peak, exiting
  const [particles, setParticles] = useState([]);
  const [countdown, setCountdown] = useState(4.5);
  const countdownIntervalRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Generate swirling particles
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      const angle = (Math.PI * 2 * i) / 50;
      const distance = 200 + Math.random() * 300;
      newParticles.push({
        id: i,
        angle,
        distance,
        speed: 0.5 + Math.random() * 1.5,
        size: 2 + Math.random() * 4
      });
    }
    setParticles(newParticles);

    // Monitor fuel level - close immediately if fuel reaches zero
    const fuelCheckInterval = setInterval(() => {
      if (fuel <= 0 && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        if (onCompleteRef.current) onCompleteRef.current();
      }
    }, 100);

    // Countdown timer (updates every 100ms)
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        const newValue = prev - 0.1;
        if (newValue <= 0) {
          return 0;
        }
        return newValue;
      });
    }, 100);

    // Phase transitions
    const peakTimer = setTimeout(() => setPhase('peak'), 1500);
    const exitTimer = setTimeout(() => setPhase('exiting'), 3000);
    const completeTimer = setTimeout(() => {
      // Complete after countdown finishes
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        if (onCompleteRef.current) onCompleteRef.current();
      }
    }, 4500); // 4.5 seconds total duration

    return () => {
      clearInterval(fuelCheckInterval);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      clearTimeout(peakTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount. fuel is intentionally excluded to prevent re-running effect

  return createPortal(
    <div className={`gravity-well-overlay ${phase}`}>
      {/* Black hole center */}
      <div className="black-hole-center">
        <div className="event-horizon"></div>
        <div className="accretion-disk"></div>
      </div>

      {/* Swirling particles */}
      <div className="particle-field">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="gravity-particle"
            style={{
              '--angle': `${particle.angle}rad`,
              '--distance': `${particle.distance}px`,
              '--speed': `${particle.speed}s`,
              '--size': `${particle.size}px`,
              animation: `gravity-particle-orbit ${particle.speed}s linear infinite ${particle.id * 0.02}s`
            }}
          />
        ))}
      </div>

      {/* Gravitational lensing effect */}
      <div className="lensing-ring ring-1"></div>
      <div className="lensing-ring ring-2"></div>
      <div className="lensing-ring ring-3"></div>

      {/* Status text */}
      <div className="gravity-status">
        {/* Countdown Timer */}
        <div className="countdown-timer">
          <div className="countdown-value">{countdown.toFixed(1)}s</div>
          <div className="countdown-label">GRAVITY FIELD DURATION</div>
        </div>
        
        {phase === 'entering' && (
          <div className="status-text entering">
            <div className="status-icon">🌀</div>
            <div className="status-message">ENTERING GRAVITATIONAL FIELD</div>
          </div>
        )}
        {phase === 'peak' && (
          <div className="status-text peak">
            <div className="status-icon">⚫</div>
            <div className="status-message">MAXIMUM GRAVITATIONAL STRESS</div>
          </div>
        )}
        {phase === 'exiting' && (
          <div className="status-text exiting">
            <div className="status-icon">✨</div>
            <div className="status-message">SLINGSHOT TRAJECTORY ACHIEVED</div>
          </div>
        )}
      </div>

      {/* Distortion overlay */}
      <div className="distortion-overlay"></div>
    </div>,
    document.body
  );
};

export default GravityWellEffect;
